import {format, isValid, parseISO} from 'date-fns'
import {useMemo} from 'react'
import {
  EventOverrideConfig,
  ListVariable,
  ListVariableValue,
  SimpleVariable,
  SimpleVariableValue,
  StaticVariable,
  Variable,
  VariableValue
} from '../../../common/types/variable-engine'
import {
  validNameRegex,
  validProfileVarName
} from '../../../common/variable-engine'
import {useVariablePaths} from '../../../providers/variables/provider'
import {VariableAction} from './actions'

export const validateName = (
  {name, newName}: {name: string; newName: string},
  existingNames: string[],
  mode: 'custom' | 'user-profile'
) => {
  if (!newName.match(validNameRegex)) {
    return 'Variable names must be alphanumeric.'
  }
  if (name !== newName && existingNames.includes(newName)) {
    return 'A variable with this name already exists.'
  }

  if (mode === 'custom' && newName.startsWith('profile.')) {
    return "A custom variable cannot start with 'profile.'"
  }

  return undefined
}

export const validateVariable = (variable: Variable) => {
  const validationErrors: {[field: string]: string | undefined} = {
    // Start with no errors, this enables these to reset on subsequent validations
    localStateDefault: undefined,
    dateFormatterOption: undefined
  }
  if (variable.type === 'date') {
    if (variable.default && !isValid(parseISO(variable.default as string))) {
      validationErrors.localStateDefault = 'Please enter a valid date'
    }
    if (
      variable.defaultFormatter === 'date' &&
      variable.defaultFormatterOption
    ) {
      try {
        format(new Date(), variable.defaultFormatterOption)
      } catch (e) {
        validationErrors.dateFormatterOption = 'Invalid date format'
      }
    }
  }
  return validationErrors
}

export const useProfileVariableNames = (): string[] => {
  const variablePaths = useVariablePaths()

  const profileVariableNames = useMemo(() => {
    return variablePaths.filter(p => p.match(validProfileVarName))
  }, [variablePaths])

  return profileVariableNames
}

export function getFlatValue(
  value: SimpleVariable['default']
): SimpleVariableValue | undefined
export function getFlatValue(
  value: ListVariable['default']
): ListVariableValue | undefined
export function getFlatValue(
  value: StaticVariable['default']
): VariableValue | undefined {
  return typeof value === 'object' && 'value' in value ? value.value : value
}

//-------------Variable session storage-------------
export type allowEventOverrideVariableTypes =
  | 'string'
  | 'number'
  | 'date'
  | 'boolean'
  | 'list'
  | 'script'
export const storeDefaultInSession = (
  variableType: allowEventOverrideVariableTypes,
  variableName: string | undefined,
  variableDefaultValue:
    | string
    | number
    | boolean
    | ListVariableValue
    | undefined,
  providerId: string | undefined
) => {
  // only store in session if id and version is present
  if (!providerId) return
  const defaultKey = `variableForm:default:${providerId}:${variableName}:${variableType}`
  if (variableDefaultValue === undefined) {
    // storing undefined is same as removing
    sessionStorage.removeItem(defaultKey)
    return
  }
  sessionStorage.setItem(defaultKey, JSON.stringify(variableDefaultValue))
}

export const restoreDefaultFromSession = (
  variableType: allowEventOverrideVariableTypes,
  variableName: string | undefined,
  providerId: string | undefined,
  dispatch: React.Dispatch<VariableAction>
) => {
  if (!providerId) return
  const defaultKey = `variableForm:default:${providerId}:${variableName}:${variableType}`
  const sessionValueString = sessionStorage.getItem(defaultKey)
  const sessionValue =
    sessionValueString === null ? undefined : JSON.parse(sessionValueString)
  switch (variableType) {
    case 'number':
    case 'string':
    case 'boolean':
    case 'date': {
      dispatch({
        type: 'setDefaultValue',
        payload: sessionValue
      })
      break
    }
    case 'list': {
      dispatch({type: 'setListDefault', payload: sessionValue})
      break
    }
    default:
      return
  }
}

export const storeRequiredInSession = (
  variableType: allowEventOverrideVariableTypes,
  variableName: string | undefined,
  variableEventOverrideRequiredValue: boolean | undefined,
  providerId: string | undefined
) => {
  if (!providerId) return

  const defaultKey = `variableForm:required:${providerId}:${variableName}:${variableType}`
  if (variableEventOverrideRequiredValue === undefined) {
    // storing undefined is same as removing
    sessionStorage.removeItem(defaultKey)
    return
  }
  sessionStorage.setItem(
    defaultKey,
    JSON.stringify(variableEventOverrideRequiredValue)
  )
}

export const restoreRequiredFromSession = (
  variableType: allowEventOverrideVariableTypes,
  variableName: string | undefined,
  providerId: string | undefined,
  dispatch: React.Dispatch<VariableAction>
): boolean | undefined => {
  if (!providerId) return undefined
  const defaultKey = `variableForm:required:${providerId}:${variableName}:${variableType}`
  const sessionValueString = sessionStorage.getItem(defaultKey)
  const sessionValue =
    sessionValueString === null ? undefined : JSON.parse(sessionValueString)
  dispatch({
    type: 'setAllowEventOverrideRequired',
    payload: sessionValue
  })
  return sessionValue
}

// ------ Helpers for allowEventOverride and required fields ------
export const onAllowEventOverrideChange = ({
  variableType,
  variableName,
  currentEventOverride,
  currentDefaultValue,
  newEventOverride,
  providerId,
  dispatch
}: {
  variableType: allowEventOverrideVariableTypes
  variableName: string | undefined
  currentEventOverride: EventOverrideConfig | undefined
  currentDefaultValue: string | number | boolean | ListVariableValue | undefined
  newEventOverride: EventOverrideConfig
  providerId: string | undefined
  dispatch: React.Dispatch<VariableAction>
}) => {
  dispatch({
    type: 'setAllowEventOverride',
    payload: !!newEventOverride?.disabled
  })
  if (!newEventOverride.disabled) {
    const sessionRequiredValue = restoreRequiredFromSession(
      variableType,
      variableName,
      providerId,
      dispatch
    )
    if (sessionRequiredValue) {
      storeDefaultInSession(
        variableType,
        variableName,
        currentDefaultValue,
        providerId
      )
      dispatch({
        type: 'setDefaultValue',
        payload: undefined
      })
    }
  } else {
    storeRequiredInSession(
      variableType,
      variableName,
      currentEventOverride?.required,
      providerId
    )
    if (currentEventOverride?.required) {
      restoreDefaultFromSession(
        variableType,
        variableName,
        providerId,
        dispatch
      )
    }
    dispatch({
      type: 'setAllowEventOverrideRequired',
      payload: undefined
    })
  }
}

export const onRequiredChange = ({
  variableType,
  newRequiredValue,
  currentDefaultValue,
  providerId,
  variableName,
  dispatch
}: {
  variableType: allowEventOverrideVariableTypes
  newRequiredValue: boolean
  currentDefaultValue: string | number | boolean | ListVariableValue
  providerId: string | undefined
  variableName: string | undefined
  dispatch: React.Dispatch<VariableAction>
}) => {
  dispatch({
    type: 'setAllowEventOverrideRequired',
    payload: newRequiredValue
  })
  // Remember the updated "required" value. It is used to persist the user selected
  // value when "eventOverride" is toggled.
  storeRequiredInSession(
    variableType,
    variableName,
    newRequiredValue,
    providerId
  )

  // If user selected "required" to be true, default value should be undefined.
  // Remember "default" value. Otherwise restore "default" value.
  if (newRequiredValue) {
    storeDefaultInSession(
      variableType,
      variableName,
      currentDefaultValue,
      providerId
    )
    dispatch({type: 'setDefaultValue', payload: undefined})
  } else {
    restoreDefaultFromSession(variableType, variableName, providerId, dispatch)
  }
}
